import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import adminApiClient from '../../api/adminApiClient';
import config from '../../utils/config';

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subscriptionType: 'free',
    subscriptionExpiry: '',
    active: true
  });

  // Load user data
  const fetchUserData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get user details
      const endpoint = config.ADMIN_ENDPOINTS.USER_DETAILS?.(userId) || `/admin/users/${userId}`;
      const response = await adminApiClient.get(endpoint);
      
      if (response.status === 'success') {
        const userData = response.data;
        setUser(userData);
        setFormData({
          name: userData.name || '',
          email: userData.email || '',
          subscriptionType: userData.subscriptionType || 'free',
          subscriptionExpiry: userData.subscriptionExpiry ? new Date(userData.subscriptionExpiry).toISOString().slice(0, -8) : '',
          active: userData.active !== undefined ? userData.active : true
        });
        
        // Get user's projects
        const projectsEndpoint = config.ADMIN_ENDPOINTS.USER_PROJECTS?.(userId) || `/admin/users/${userId}/projects`;
        const projectsResponse = await adminApiClient.get(projectsEndpoint);
        
        if (projectsResponse.status === 'success') {
          setProjects(projectsResponse.data || []);
        }
      } else {
        setError('Failed to fetch user details');
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      setError('An error occurred while fetching user details');
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchUserData();
  }, [userId]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Save user changes
  const handleSaveUser = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Update user endpoint
      const endpoint = config.ADMIN_ENDPOINTS.UPDATE_USER?.(userId) || `/admin/users/${userId}`;
      const response = await adminApiClient.patch(endpoint, formData);
      
      if (response.status === 'success') {
        setUser(response.data);
        setIsEditMode(false);
        // Show success message
        alert('User updated successfully');
      } else {
        setError('Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setError('An error occurred while updating user details');
    } finally {
      setIsLoading(false);
    }
  };

  // Get formatted date string
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Navigate to project details
  const handleViewProject = (projectId) => {
    navigate(`/admin/projects/${projectId}`);
  };

  return (
    <AdminLayout title="User Details">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex justify-between items-center">
          <div className="flex items-center">
            <button 
              onClick={() => navigate('/admin/users')}
              className="mr-4 text-gray-600 hover:text-gray-900 btn-secondary btn text-smd"
            >
              ← Back to Users
            </button>
            <h1 className="text-2xl font-bold ml-2">User Details</h1>
          </div>
          {!isEditMode && (
            <button
              onClick={() => setIsEditMode(true)}
              className="btn btn-primary text-smd"
            >
              Edit User
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 bg-red-100 text-red-700 p-4 rounded-md">
            {error}
          </div>
        )}

        {isLoading && !user ? (
          <div className="text-center py-8">
            <div className="loader mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading user data...</p>
          </div>
        ) : user ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* User Information */}
            <div className="lg:col-span-1">
              <div className="bg-white shadow rounded-lg p-2">
                <h2 className="text-xl font-semibold mb-4">User Information</h2>
                
                {isEditMode ? (
                  <form onSubmit={handleSaveUser}>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="form-input rounded-md w-full"
                        required
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="form-input rounded-md w-full"
                        required
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Type</label>
                      <select
                        name="subscriptionType"
                        value={formData.subscriptionType}
                        onChange={handleInputChange}
                        className="form-select rounded-md w-full text-smd"
                      >
                        <option value="free">Free</option>
                        <option value="basic">Basic</option>
                        <option value="premium">Premium</option>
                      </select>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Expiry</label>
                      <input
                        type="datetime-local"
                        name="subscriptionExpiry"
                        value={formData.subscriptionExpiry}
                        onChange={handleInputChange}
                        className="form-input rounded-md w-full"
                      />
                    </div>
                    
                    <div className="mb-4 flex items-center">
                      <input
                        type="checkbox"
                        name="active"
                        id="active"
                        checked={formData.active}
                        onChange={handleInputChange}
                        className="form-checkbox h-4 w-4 text-indigo-600"
                      />
                      <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
                        Account Active
                      </label>
                    </div>
                    
                    <div className="flex justify-end mt-6">
                      <button
                        type="button"
                        onClick={() => setIsEditMode(false)}
                        className="btn btn-secondary mr-2 text-smd"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary text-smd"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-m font-medium text-gray-500">Name</h3>
                      <p className="mt-1 text-smd text-gray-900">{user.name}</p>
                    </div>
                    
                    <div>
                      <h3 className="text-m font-medium text-gray-500">Email</h3>
                      <p className="mt-1 text-smd text-gray-900">{user.email}</p>
                    </div>
                    
                    <div>
                      <h3 className="text-m font-medium text-gray-500">Organization</h3>
                      <p className="mt-1 text-smd text-gray-900">{user.organization || 'Not specified'}</p>
                    </div>
                    
                    <div>
                      <h3 className="text-m font-medium text-gray-500">Subscription</h3>
                      <p className="mt-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                          ${user.subscriptionType === 'premium' ? 'bg-purple-100 text-purple-800' : 
                            user.subscriptionType === 'basic' ? 'bg-blue-100 text-blue-800' : 
                            'bg-gray-100 text-gray-800'}`}
                        >
                          {user.subscriptionType?.charAt(0).toUpperCase() + user.subscriptionType?.slice(1) || 'Free'}
                        </span>
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-m font-medium text-gray-500">Subscription Expiry</h3>
                      <p className="mt-1 text-smd text-gray-900">
                        {user.subscriptionExpiry ? formatDate(user.subscriptionExpiry) : 'Not applicable'}
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-m font-medium text-gray-500">Status</h3>
                      <div className="mt-1 flex">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mr-2 
                          ${user.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                        >
                          {user.active ? 'Active' : 'Inactive'}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                          ${user.emailVerified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}
                        >
                          {user.emailVerified ? 'Verified' : 'Unverified'}
                        </span>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-m font-medium text-gray-500">Joined</h3>
                      <p className="mt-1 text-smd text-gray-900">{formatDate(user.createdAt)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* User Projects */}
            <div className="lg:col-span-2">
              <div className="bg-white shadow rounded-lg p-2">
                <h2 className="text-xl font-semibold mb-4">User Projects</h2>
                
                {isLoading && projects.length === 0 ? (
                  <div className="text-center py-4">
                    <div className="loader mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading projects...</p>
                  </div>
                ) : projects.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">This user has no projects</p>
                  </div>
                ) : (
                  <div className="overflow-hidden border border-gray-200 rounded-md">
                    <table className="w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{borderBottom: '2px solid #ccc'}}>
                            Project Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{borderBottom: '2px solid #ccc'}}>
                            Created
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{borderBottom: '2px solid #ccc'}}>
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{borderBottom: '2px solid #ccc'}}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {projects.map((project) => (
                          <tr key={project._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap" align="center">
                              <div className="text-smd font-medium text-gray-900">{project.name}</div>
                              <div className="text-sm text-gray-500">{project.description?.substring(0, 60) || 'No description'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" align="center">
                              <div className="text-smd text-gray-500">{formatDate(project.createdAt)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" align="center">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                project.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {project.active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" align="center">
                              <button
                                onClick={() => handleViewProject(project._id)}
                                className="text-indigo-600 hover:text-indigo-900 mr-2 btn btn-secondary text-smd"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <p className="text-gray-500">User not found</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default UserDetails; 